// React bindings for LiveStreamClient. One client instance per
// provider; child components read derived state via `useLiveStream`.
//
// The provider owns the long-lived WebSocket so navigating between
// routes doesn't churn the connection — TanStack Router unmounts page
// components freely, but the persistent app shell that renders
// `<LiveStreamProvider>` stays mounted.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  LiveStreamClient,
  resolveReceivedAt,
  type CommandPayload,
  type LiveSamplePayload,
  type LiveStreamState,
  type PenStatusPayload,
  type PublisherStatusPayload,
} from "./client";

interface LiveStreamContextValue {
  state: LiveStreamState;
  /** Most recent live sample, or null until first frame. */
  lastSample: { sample: LiveSamplePayload; receivedAt: number } | null;
  /** Most recent pen status, or null until the publisher reports. */
  lastStatus: { status: PenStatusPayload; receivedAt: number } | null;
  /** Authoritative iOS-publisher presence from the hub: true/false once the
   *  server reports it, null until then (or on a server too old to send it). */
  publisherOnline: boolean | null;
  /** Last server error frame, if any. */
  lastError: { code: string; message: string } | null;
  /** Send a command to publishers (e.g. reconnect_pen). */
  sendCommand(payload: CommandPayload): string;
}

const Ctx = createContext<LiveStreamContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
  /** Override the default `/v1/streams/ws` endpoint (mainly for tests). */
  path?: string;
  /** When false, the provider mounts but never opens the socket. Lets the
   *  shell render in non-browser environments / Storybook without errors. */
  enabled?: boolean;
}

export function LiveStreamProvider({
  children,
  path,
  enabled = true,
}: ProviderProps) {
  const clientRef = useRef<LiveStreamClient | null>(null);

  // Reactive snapshots derived from the client's listeners. Kept in
  // refs internally so re-renders don't churn the listener bindings.
  const [state, setState] = useState<LiveStreamState>("idle");
  const [lastSample, setLastSample] =
    useState<LiveStreamContextValue["lastSample"]>(null);
  const [lastStatus, setLastStatus] =
    useState<LiveStreamContextValue["lastStatus"]>(null);
  const [publisherOnline, setPublisherOnline] = useState<boolean | null>(null);
  const [lastError, setLastError] =
    useState<LiveStreamContextValue["lastError"]>(null);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    const client = new LiveStreamClient({ path });
    clientRef.current = client;

    const offState = client.onState(setState);
    const offSample = client.onSample((sample, ts, isReplay) =>
      setLastSample({
        sample,
        receivedAt: resolveReceivedAt(isReplay ?? false, ts, Date.now()),
      }),
    );
    const offStatus = client.onStatus((status, ts, isReplay) =>
      setLastStatus({
        status,
        receivedAt: resolveReceivedAt(isReplay ?? false, ts, Date.now()),
      }),
    );
    const offPublisherStatus = client.onPublisherStatus(
      (status: PublisherStatusPayload) => setPublisherOnline(status.online),
    );
    const offError = client.onError((err) =>
      setLastError({ code: err.code, message: err.message }),
    );

    client.connect();

    return () => {
      offState();
      offSample();
      offStatus();
      offPublisherStatus();
      offError();
      client.disconnect();
      clientRef.current = null;
    };
  }, [enabled, path]);

  const value = useMemo<LiveStreamContextValue>(
    () => ({
      state,
      lastSample,
      lastStatus,
      publisherOnline,
      lastError,
      sendCommand: (payload) => {
        const c = clientRef.current;
        if (!c) return "";
        return c.sendCommand(payload);
      },
    }),
    [state, lastSample, lastStatus, publisherOnline, lastError],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Read live-stream state from the nearest provider. */
export function useLiveStream(): LiveStreamContextValue {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error("useLiveStream must be called inside <LiveStreamProvider>");
  }
  return v;
}
